import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useHistory } from 'react-router';
import { ELSToastService } from '@els/els-component-toast-react';
import { AppContext, AppContextState } from '../../components/app/AppProvider.component';
import { AppState } from '../../components/app/app-state.types';
import {
  fetchChatAction,
  handleChatClickAction,
} from '../../components/app/app-state-actions.utilities';
import { RoutePath } from '../../components/app/app.constants';
import { ELSDropDownOption } from '../../components/els.components';
import {
  ChatDto,
  ChatUserRoleDto,
  ContextRequestDto,
  LibraryListResponseDto,
  LibraryQueryParamsDto,
  LibrarySelectionDto,
  LibrarySortDto,
} from '../../apis/nhe-ai-chat-service/nhe-ai-chat-service.dtos';
import { preserveAdminOverrideParam } from '../../utilities/app.utilities';
import {
  getBookTitleWithEdition,
  getChatContextsForRequest,
  getOrderedPurchasedEntitlements,
  toastApiError
} from '../ai-chat/ai-chat.utilities';
import {
  ALL_BOOKS,
  LIBRARY_SEARCH_DEBOUNCE_MS,
  SEGMENT_SIZE_ALL,
} from './library-page.constants';
import {
  getLibraryResultsKey,
  getLibrarySelection,
  LIBRARY_SELECTION_RESET,
  LibrarySelectionState,
} from './library-page.utilities';

// What every library row carries, whatever the item is, for its date and options menu
export type LibraryRowDto = {
  id?: number;
  createdAt?: string;
  chatId: number;
  chatTitle: string;
  entryId: number;
  entryText: string;
};

export type LibraryPageState<T> = LibrarySelectionState & {
  searchInput: string;
  // Shows or hides the book, date and number of items filters; the search and the chat and entry pills always stay visible
  isFiltersOpen: boolean;
  // The row whose options menu is open. Keyed by row, not one shared flag, because opening one menu
  // closes the one that was open and a shared flag would close the new menu with it.
  openMenuRowId: number;
  // Typed text that is not a date: the filter is not applied, so the box says why
  isCreatedFromInvalid: boolean;
  isCreatedToInvalid: boolean;
  // Remounts the date boxes, which keep their own text, when Clear filters empties them
  datesKey: number;
  // 0-based, as NACS pages are
  page: number;
  response: LibraryListResponseDto<T>;
  // getLibraryResultsKey of the request the response above came from, or the one that failed
  resultsKey: string;
};

type LibraryPanelState<P> = {
  // What the page's View (or Take) opened the panel with; null while the panel is closed
  content: P;
  // Remounts the panel on every open, so each opens fresh
  key: number;
};

type UseLibraryPageProps<T> = {
  // The page's list action from app-state-actions.utilities, e.g. fetchQuizQuestionsAction
  fetchList: (props: AppContextState & {
    params: LibraryQueryParamsDto;
    userRole: ChatUserRoleDto;
    context: ContextRequestDto[];
  }) => Promise<LibraryListResponseDto<T>>;
  pageSize: number;
  // Whether the page's nav item shows; the page redirects to the chat when it does not
  isShowNav: (appState: AppState) => boolean;
  // The plain text a row shows and its options menu is named by
  getRowText: (row: T) => string;
  toastService: ELSToastService;
};

const defaultState: LibraryPageState<never> = {
  searchInput: '',
  search: '',
  isbn: ALL_BOOKS,
  chatFilter: null,
  entryFilter: null,
  isFiltersOpen: false,
  openMenuRowId: null,
  createdFrom: null,
  createdTo: null,
  isCreatedFromInvalid: false,
  isCreatedToInvalid: false,
  datesKey: 0,
  sort: LibrarySortDto.DESC,
  segmentSize: SEGMENT_SIZE_ALL,
  segment: '0',
  page: 0,
  response: null,
  resultsKey: null,
};

// What useLibraryPage returns, which a page hands to LibraryPageContent. P is what the page opens its panel with.
export type LibraryPage<T extends LibraryRowDto, P = unknown> = {
  state: LibraryPageState<T>;
  mergeState: (newState: Partial<LibraryPageState<T>>) => void;
  listRef: React.RefObject<HTMLDivElement>;
  bodyRef: React.RefObject<HTMLDivElement>;
  pageSize: number;
  // The selection View and Take open the panel with
  selection: LibrarySelectionDto;
  isResultsCurrent: boolean;
  // The response, or null while it does not match the current selection
  response: LibraryListResponseDto<T>;
  // How many items the current selection holds; 0 until its response arrives
  itemCount: number;
  // Each row's plain text, converted once per fetched page
  rowTextById: Map<number, string>;
  // Segment choices are built from the matches before the window, which the response reports
  totalMatches: number;
  isSegmentSized: boolean;
  // The chat and entry pills are always shown, so only the filters hidden with the toggle count towards its badge
  hiddenFilterCount: number;
  hasFilters: boolean;
  // A chosen number of items is cleared along with the filters, though it does not count as one
  canClearFilters: boolean;
  // False when there is nothing to choose besides "All books", so the Book filter is hidden
  hasBookOptions: boolean;
  bookOptions: ELSDropDownOption[];
  clearFilters: () => void;
  toggleFilters: () => void;
  clearChatFilter: () => void;
  clearEntryFilter: () => void;
  handleChatTitleClick: (row: T) => void;
  handleEntryTextClick: (row: T) => void;
  handleOpenChatClick: (row: T) => void;
  handleSegmentSizeChange: (segmentSize: string) => void;
  openMenu: (row: T) => void;
  closeMenu: (row: T) => void;
  panelContent: P;
  panelKey: number;
  openPanel: (content: P) => void;
  closePanel: () => void;
  // For the panel's onError. The page toasts and closes the panel: a toast raised by the panel's own toast
  // service would be unmounted along with it.
  handlePanelError: () => void;
};

// The state, list fetch, panel state and handlers shared by the library pages (Quiz questions, Flashcards).
// Each page renders its own panel and View/Take buttons, and the rest with LibraryPageContent.
export const useLibraryPage = <T extends LibraryRowDto, P>(props: UseLibraryPageProps<T>): LibraryPage<T, P> => {

  const {
    appState,
    setAppState,
    appStateRef
  } = useContext(AppContext);
  const history = useHistory();
  const [state, setState] = useState<LibraryPageState<T>>(defaultState);
  const [panelState, setPanelState] = useState<LibraryPanelState<P>>({ content: null, key: 0 });
  const latestRequestIdRef = useRef(0);
  const listRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  const mergeState = (newState: Partial<LibraryPageState<T>>) => {
    setState((prevState) => ({ ...prevState, ...newState }));
  };

  // The page is only reachable while its nav item shows, so a bookmark or a flag-off user lands on the chat instead
  useEffect(() => {
    if (!props.isShowNav(appState)) {
      history.replace(preserveAdminOverrideParam(RoutePath.AI_CHAT, history.location.search));
    }
  }, [appState.featureFlagsGrouped, appState.courseSectionId]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const search = state.searchInput.trim();
      setState((prevState) => (prevState.search === search ? prevState : { ...prevState, search, ...LIBRARY_SELECTION_RESET }));
    }, LIBRARY_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [state.searchInput]);

  useEffect(() => {
    latestRequestIdRef.current += 1;
    const requestId = latestRequestIdRef.current;
    const resultsKey = getLibraryResultsKey(state);
    const isCurrent = () => requestId === latestRequestIdRef.current;

    props.fetchList({
      appState,
      appStateRef,
      setAppState,
      userRole: appState.chatUserRole,
      context: getChatContextsForRequest(appState),
      params: {
        ...getLibrarySelection(state),
        page: state.page,
        pageSize: props.pageSize,
      },
    })
      .then((response) => {
        if (isCurrent()) {
          mergeState({ response, resultsKey });
        }
      })
      .catch(() => {
        if (isCurrent()) {
          mergeState({ response: null, resultsKey });
          toastApiError(props.toastService);
        }
      });
  }, [
    state.search,
    state.isbn,
    state.chatFilter,
    state.entryFilter,
    state.createdFrom,
    state.createdTo,
    state.sort,
    state.segmentSize,
    state.segment,
    state.page,
    appState.chatUserRole,
  ]);

  // A new page or selection should start at its first row. The list scrolls on its own, except in a short viewport
  // where the page body scrolls instead (see _library-page.scss). The body is only scrolled back when the list's top
  // is above its visible area, as after paging from the bottom: a student who scrolled the body down to a filter,
  // which sits above the list, stays on that filter when its results arrive.
  useEffect(() => {
    const list = listRef.current;
    const body = bodyRef.current;
    list?.scrollTo?.({ top: 0 });
    if (!list || !body) {
      return;
    }
    const listTop = list.getBoundingClientRect().top - body.getBoundingClientRect().top + body.scrollTop;
    if (body.scrollTop > listTop) {
      body.scrollTo?.({ top: listTop });
    }
  }, [state.page, state.resultsKey]);

  const handleChatTitleClick = (row: T) => {
    mergeState({
      chatFilter: { chatId: row.chatId, title: row.chatTitle },
      ...LIBRARY_SELECTION_RESET
    });
  };

  const openChat = (chat: ChatDto) => {
    handleChatClickAction({
      appState,
      appStateRef,
      setAppState,
      chat,
      sendClickEvent: true,
      checkLoading: true,
      toastService: props.toastService,
    });
    history.push(preserveAdminOverrideParam(RoutePath.AI_CHAT, history.location.search));
  };

  const handleOpenChatClick = (row: T) => {
    const cachedChat = appState.chats?.find((item) => item.id === row.chatId);
    if (cachedChat || !appState.chats) {
      // Until the chat list has loaded (null) the chat page loads it, so an id and title are enough to open it
      openChat(cachedChat ?? ({ id: row.chatId, title: row.chatTitle } as ChatDto));
      return;
    }
    // The chat is not in the loaded list, and the chat page needs it cached to continue the conversation
    fetchChatAction({ appState, appStateRef, setAppState, chatId: row.chatId })
      .then((chat) => {
        setAppState((prevState) => ({
          ...prevState,
          chats: [chat, ...(prevState.chats ?? []).filter((item) => item.id !== chat.id)],
        }));
        openChat(chat);
      })
      .catch(() => toastApiError(props.toastService));
  };

  // Resets every filter, the number of items and the segment; the sort is kept
  const clearFilters = () => {
    setState((prevState) => ({
      ...prevState,
      searchInput: defaultState.searchInput,
      search: defaultState.search,
      isbn: defaultState.isbn,
      createdFrom: null,
      createdTo: null,
      isCreatedFromInvalid: false,
      isCreatedToInvalid: false,
      datesKey: prevState.datesKey + 1,
      chatFilter: null,
      entryFilter: null,
      ...LIBRARY_SELECTION_RESET
    }));
  };

  // Hiding the filters unmounts the date boxes and the text typed in them. Typed text that was not a date is
  // dropped with them, rather than kept as an invalid box that would come back reading "Invalid date" and keep
  // offering Clear filters for a filter the badge cannot show.
  const toggleFilters = () => {
    setState((prevState) => (prevState.isFiltersOpen
      ? { ...prevState, isFiltersOpen: false, isCreatedFromInvalid: false, isCreatedToInvalid: false }
      : { ...prevState, isFiltersOpen: true }));
  };

  const clearChatFilter = () => {
    mergeState({ chatFilter: null, ...LIBRARY_SELECTION_RESET });
  };

  const handleEntryTextClick = (row: T) => {
    mergeState({
      entryFilter: { entryId: row.entryId, text: row.entryText },
      ...LIBRARY_SELECTION_RESET
    });
  };

  const clearEntryFilter = () => {
    mergeState({ entryFilter: null, ...LIBRARY_SELECTION_RESET });
  };

  // A new size starts at its first segment; the previous segment index belonged to the old size
  const handleSegmentSizeChange = (segmentSize: string) => {
    mergeState({ segmentSize, segment: '0', page: 0 });
  };

  const openMenu = (row: T) => {
    mergeState({ openMenuRowId: row.id });
  };

  // Only closes the menu if it is still this row's, so closing the old menu cannot close a newly opened one
  const closeMenu = (row: T) => {
    setState((prevState) => (
      prevState.openMenuRowId === row.id ? { ...prevState, openMenuRowId: null } : prevState
    ));
  };

  const openPanel = (content: P) => {
    setPanelState((prevState) => ({ content, key: prevState.key + 1 }));
  };

  const closePanel = () => {
    setPanelState((prevState) => ({ ...prevState, content: null }));
  };

  const handlePanelError = () => {
    toastApiError(props.toastService);
    closePanel();
  };

  const bookOptions: ELSDropDownOption[] = [
    { name: 'All books', value: ALL_BOOKS },
    ...getOrderedPurchasedEntitlements(appState.entitlements)
      .filter(({ title }) => !!title)
      .map(({ isbn, title, edition }) => ({ name: getBookTitleWithEdition(title, edition), value: isbn })),
  ];

  // Apps without books (Nora, Osmosis AI, AI Coach) have nothing to choose besides "All books"
  const hasBookOptions = bookOptions.length > 1;

  const isResultsCurrent = state.resultsKey === getLibraryResultsKey(state);
  const response = isResultsCurrent ? state.response : null;
  // Converting markdown is costly, so each row's text is converted once per fetched page, not on every render
  const rowTextById = useMemo(
    () => new Map((state.response?.content ?? []).map((row) => [row.id, props.getRowText(row)])),
    [state.response]
  );
  const isSegmentSized = state.segmentSize !== SEGMENT_SIZE_ALL;
  const hiddenFilterCount = [state.isbn, state.createdFrom || state.createdTo, isSegmentSized].filter(Boolean).length;
  const hasFilters = !!(state.search || state.isbn || state.chatFilter || state.entryFilter
    || state.createdFrom || state.createdTo);

  return {
    state,
    mergeState,
    listRef,
    bodyRef,
    pageSize: props.pageSize,
    selection: getLibrarySelection(state),
    isResultsCurrent,
    response,
    itemCount: response?.totalItems ?? 0,
    rowTextById,
    totalMatches: state.response?.totalMatches ?? 0,
    isSegmentSized,
    hiddenFilterCount,
    hasFilters,
    canClearFilters: hasFilters || isSegmentSized || state.isCreatedFromInvalid || state.isCreatedToInvalid,
    hasBookOptions,
    bookOptions,
    clearFilters,
    toggleFilters,
    clearChatFilter,
    clearEntryFilter,
    handleChatTitleClick,
    handleEntryTextClick,
    handleOpenChatClick,
    handleSegmentSizeChange,
    openMenu,
    closeMenu,
    panelContent: panelState.content,
    panelKey: panelState.key,
    openPanel,
    closePanel,
    handlePanelError,
  };
};
